import { Box, Divider, IconButton, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Close } from '@material-ui/icons';
import { PushPin } from './pushPinIcons';
import { HtmlMessageContent } from '@roc/ui';
import { observer } from 'mobx-react';
import { CommunicationStore } from '../stores';
import { getChatFormattedDate } from '../helper';

const useStyles = makeStyles(theme => ({
  header: {
    borderLeft: '1px solid rgba(0, 0, 0, .125)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
  },
  messageCard: {
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid rgba(0, 0, 0, .08)',
  },
  messageCardClickable: {
    flex: 1,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      borderRadius: 4,
    },
  },
  messageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  author: {
    fontWeight: 'bold',
    fontSize: 13,
  },
  timestamp: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  body: {
    marginTop: theme.spacing(0.5),
    fontSize: 13,
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 120,
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
}));

interface PinnedMessagesProps {
  handleClose: () => void;
  communicationStore: CommunicationStore;
  onScrollToMessage: (messageSid: string) => void;
}

export const PinnedMessages = observer(({ handleClose, communicationStore, onScrollToMessage }: PinnedMessagesProps) => {
  const classes = useStyles();
  const { currentConversationSid, currentChatRoomId } = communicationStore;
  const pinned = communicationStore.pinnedMessages[currentConversationSid] ?? [];

  return (
    <Box>
      <Box className={classes.header}>
        <Box>
          <Typography variant="h6" className={classes.title}>
            <PushPin style={{ marginRight: 10 }} /> Pinned Messages
          </Typography>
        </Box>
        <Box m={'auto 20px'}>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </Box>
      <Divider />
      {pinned.length === 0 ? (
        <Box className={classes.emptyState}>No pinned messages.</Box>
      ) : (
        pinned.map(item => (
          <Box key={item.messageSid} className={classes.messageCard}>
            <Box className={classes.messageHeader}>
              <Box
                className={classes.messageCardClickable}
                onClick={() => { onScrollToMessage(item.messageSid); handleClose(); }}
              >
                <Typography className={classes.author}>{item.messageAuthor}</Typography>
                {item.messageDate && (
                  <Typography className={classes.timestamp}>
                    {getChatFormattedDate(new Date(item.messageDate))}
                  </Typography>
                )}
                <Box className={classes.body}>
                  <HtmlMessageContent id={`pinned-${item.messageSid}`} html={item.messageBody} />
                </Box>
              </Box>
              <IconButton
                size="small"
                style={{ flexShrink: 0, color: 'inherit' }}
                onClick={() => communicationStore.unpinMessage(currentChatRoomId, currentConversationSid, item.messageSid)}
              >
                <PushPin fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
});
